import { useEffect, useReducer, useState } from "react"
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter"
import { useLocation } from "wouter"
import { paths } from "../../../paths"
import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import cx from "clsx"
import {
  BINDINGS,
  FORM_CONTROLS,
  bindingsFor,
  instancesOf,
  isBoundField,
  type BindingKey,
  type FormControlKey,
  type FormModuleDetail,
  FormModuleErrorCode,
} from "shared"
import { fieldName } from "@rawa/form-modules/fields"
import { newBoundField, newField } from "../utils/moduleFields"
import { useOffCanvas } from "../../../dragAndDrop/offCanvas"
import { useCanvas } from "../../../dragAndDrop/useCanvas"
import {
  eventForDrop,
  isFieldDrag,
  isFromLibrary,
  isPlacedField,
  libraryBinding,
  libraryControl,
  moduleCanvas,
} from "../utils/moduleDrag"
import { FORM_MODE, recordOf } from "../utils/boundFields"
import {
  canSave,
  moduleReducer,
  fieldsNeedingAttention,
  hasUnsavedChanges,
  hasValidDetails,
  seedModuleState,
  type ModuleEdit,
  type ModuleState,
} from "../stores/moduleState"
import { MODULE_NAMES, useNameTaken } from "../hooks/useNameTaken"
import { ModuleNameFields } from "../components/ModuleNameFields"
import LayersIcon from "../../../assets/icon-layers.svg?react"
import DatabaseIcon from "../../../assets/icon-database.svg?react"
import PlusIcon from "@rawa/ui/assets/icon-plus.svg?react"
import EyeIcon from "../../../assets/icon-eye.svg?react"
import { AdminButton } from "@rawa/ui/AdminButton"
import { Tabs } from "@rawa/ui/Tabs"
import { DiscardChangesDialog } from "../components/DiscardChangesDialog"
import { FieldInspector } from "../components/FieldInspector"
import { PlacedFieldCard } from "../components/PlacedFieldCard"
import { LibraryEntry } from "../components/LibraryEntry"
import { BoundFieldProperties } from "../components/BoundFieldProperties"
import { VersionTag } from "@rawa/ui/VersionTag"
import { ModulePreviewDialog } from "../components/ModulePreviewDialog"
import { fetchFormModule, updateFormModule } from "../api"
import {
  DiscardDraftButton,
  PublishButton,
} from "../components/ModuleCanvasActions"

import { ErrorCallout } from "@rawa/ui/ErrorCallout"
import { SaveRefusal } from "../components/SaveRefusal"
import { NOT_AUTHORIZED_MESSAGE } from "../utils/notAuthorized"
import {
  ApiResponseError,
  isExpectedApiError,
  isForbidden,
} from "@rawa/api-client/ApiResponseError"
import { trackError } from "@rawa/telemetry"
import typography from "@rawa/ui/styles/typography.module.css"
import styles from "./ModuleCanvasPage.module.css"

const saveKey = (id: string) => ["save-form-module", id] as const

type SaveRequest = { sent: ModuleEdit; revision: number }

/** Why Save is held, for the footer to say, or null when nothing holds it. */
function whySaveIsHeld(state: ModuleState, nameTaken: boolean): string | null {
  if (nameTaken) return "Choose a different module name to save."
  if (!hasValidDetails(state)) return "Enter a name and description to save."
  if (!canSave(state))
    return "Some fields need attention before the module can be saved."
  return null
}

/**
 * One module's canvas. The editor seeds its working copy from the module once,
 * so it opens only on a read made since the page opened, and after any save of
 * the module has landed: a copy this browser read earlier may be older than the
 * server's, and saving from it would put the module back as it was.
 */
export function ModuleCanvasPage({ id }: { id: string }) {
  const {
    data: formModule,
    isFetchedAfterMount,
    isError,
    error,
  } = useQuery({
    queryKey: ["form-module", id],
    queryFn: ({ signal }) => fetchFormModule(id, signal),
    // The client's default retries exist for a kiosk tablet waking its Wi-Fi.
    // An administrator at a desk is told what went wrong instead.
    retry: false,
    meta: { source: "fetchFormModule", forbiddenIsExpected: true },
  })

  // A 403 is an authorization outcome with a screen of its own; anything else
  // is a failure to load.
  const forbidden = isError && isForbidden(error)
  const loadError = isError && !forbidden
  const isSaving = useIsMutating({ mutationKey: saveKey(id) }) > 0
  const mayOpen = formModule !== undefined && isFetchedAfterMount && !isSaving
  // Once open, the editor stays open through its own saves.
  const [hasOpened, setHasOpened] = useState(false)
  if (mayOpen && !hasOpened) setHasOpened(true)
  const isReady = formModule !== undefined && (hasOpened || mayOpen)

  return (
    <div className={styles.page}>
      {!isReady && !isError && (
        <div role="status" aria-label="Loading module" className={styles.panel}>
          <span className={styles.skeletonName} />
        </div>
      )}

      {forbidden && <ErrorCallout>{NOT_AUTHORIZED_MESSAGE}</ErrorCallout>}

      {loadError && <ErrorCallout>Failed to load module.</ErrorCallout>}

      {isReady && <ModuleEditor key={formModule.id} formModule={formModule} />}
    </div>
  )
}

/**
 * The field library, canvas and properties panel for one loaded module.
 *
 * The page mounts it keyed by module id, so its state is seeded from the
 * module exactly once, here, and another module gets a fresh editor. A later
 * read of the same module updates only what renders from the prop.
 */
function ModuleEditor({ formModule }: { formModule: FormModuleDetail }) {
  // The administrator's working copy of the module.
  const [state, dispatch] = useReducer(
    moduleReducer,
    formModule,
    seedModuleState,
  )
  const { details, fields, selectedId } = state
  const changed = hasUnsavedChanges(state)
  const nameTaken = useNameTaken(
    MODULE_NAMES,
    details.name,
    state.storedDetails.name,
    formModule.id,
  )
  const [previewing, setPreviewing] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [, navigate] = useLocation()
  const queryClient = useQueryClient()

  const canvas = useCanvas<HTMLElement>({
    zone: moduleCanvas,
    accepts: isFieldDrag,
    lightsFor: isFromLibrary,
  })
  useOffCanvas(isPlacedField)

  // A drop changes the module as the buttons would (see eventForDrop).
  useEffect(
    () =>
      monitorForElements({
        onDrop: ({ source, location }) => {
          const event = eventForDrop(
            fields,
            source.data,
            location.current.dropTargets[0]?.data,
          )
          if (event) dispatch(event)
        },
      }),
    [fields],
  )

  const save = useMutation({
    mutationKey: saveKey(formModule.id),
    // Named for the revision the canvas was edited from, never the page's
    // latest read, so a save cannot put back a change this canvas never showed.
    mutationFn: ({ sent, revision }: SaveRequest) =>
      updateFormModule(formModule.id, {
        name: sent.details.name,
        description: sent.details.description,
        entityLink: formModule.entityLink,
        definition: { fields: sent.fields },
        revision,
      }),
    onError: (err) => trackError(err, { source: "updateFormModule" }),
    onSuccess: (saved, { sent }) => {
      dispatch({ type: "saved", sent, module: saved })
      queryClient.setQueryData(["form-module", formModule.id], saved)
    },
  })

  /**
   * Takes the module as the server now holds it after a publish or a discard:
   * into the canvas, into the cache the page reads, and — since the list's
   * tags and the template library read what is published — out of the list's.
   */
  function adopt(module: FormModuleDetail, type: "published" | "reloaded") {
    dispatch({ type, module })
    queryClient.setQueryData(["form-module", formModule.id], module)
    void queryClient.invalidateQueries({ queryKey: ["form-modules"] })
  }

  const isChangedElsewhere =
    save.error instanceof ApiResponseError &&
    save.error.code === FormModuleErrorCode.FORM_MODULE_CHANGED
  // A 4xx carries a reason the administrator can act on, shown as sent.
  const saveError = save.error ? (
    isExpectedApiError(save.error) ? (
      <SaveRefusal error={save.error} />
    ) : (
      "Could not save the module. Please try again."
    )
  ) : null

  const [isReloadFailed, setIsReloadFailed] = useState(false)

  /**
   * Replaces the canvas with the module as it is stored now, after a save was
   * refused for someone else's: what was changed here is dropped, as the
   * message says. A reload that fails keeps the canvas as it is, and says so.
   */
  async function reload() {
    setIsReloadFailed(false)
    try {
      const stored = await queryClient.fetchQuery({
        queryKey: ["form-module", formModule.id],
        queryFn: ({ signal }) => fetchFormModule(formModule.id, signal),
        staleTime: 0,
        // As the page's own read: an administrator at a desk is told.
        retry: false,
      })
      dispatch({ type: "reloaded", module: stored })
      save.reset()
    } catch (err) {
      trackError(err, { source: "fetchFormModule" })
      setIsReloadFailed(true)
    }
  }

  const selected = fields.find((field) => field.id === selectedId) ?? null

  // Holding Save until the server's rules pass keeps a refused write from
  // reading as a failure to reach the server; the fields at fault are marked
  // on the canvas so the reason can be seen.
  const saveHeld = whySaveIsHeld(state, nameTaken)
  const saveable = saveHeld === null
  const unpublished = state.unpublishedChange
  const needsAttention = fieldsNeedingAttention(state)
  // The headings follow the name as it is typed, as a template's do.
  const shownName = details.name.trim() || state.storedDetails.name

  // What the module's answers are about decides which bound fields it is
  // offered.
  const instances = instancesOf(formModule.entityLink)
  const offered = bindingsFor(formModule.entityLink)
  // A binding shows one value, so the library holds back one already placed.
  const placed = new Set(fields.filter(isBoundField).map((f) => f.binding))

  function addField(control: FormControlKey) {
    dispatch({ type: "added", field: newField(control), at: fields.length })
  }

  function addBoundField(binding: BindingKey) {
    dispatch({
      type: "added",
      field: newBoundField(binding),
      at: fields.length,
    })
  }

  return (
    <>
      {isChangedElsewhere ? (
        <div className={styles.conflict}>
          <ErrorCallout>
            Someone has saved this module since you opened it. Reload it to see
            their changes; what you&rsquo;ve changed here won&rsquo;t be kept.
          </ErrorCallout>
          {isReloadFailed && (
            <ErrorCallout>
              Couldn&rsquo;t reload the module. Please try again.
            </ErrorCallout>
          )}
          <AdminButton variant="outline" onClick={() => void reload()}>
            Reload module
          </AdminButton>
        </div>
      ) : (
        saveError && <ErrorCallout>{saveError}</ErrorCallout>
      )}
      <div className={styles.titleRow}>
        <div className={styles.titleBlock}>
          <div className={styles.nameRow}>
            <h1 className={cx(typography.h2, styles.heading)}>{shownName}</h1>
            {state.publishedVersion === null ? (
              <span className={styles.statusTag}>Not published</span>
            ) : (
              <VersionTag version={state.publishedVersion} />
            )}
            {unpublished && <span className={styles.statusTag}>Draft</span>}
          </div>
          <p className={styles.subtitle}>{formModule.entityLinkLabel}</p>
        </div>
        <AdminButton variant="outline" onClick={() => setPreviewing(true)}>
          <EyeIcon className={styles.previewIcon} aria-hidden="true" />
          Preview
        </AdminButton>
      </div>

      <div className={styles.builder}>
        <aside className={styles.panel}>
          <section className={styles.details} aria-label="Module details">
            <p className={styles.panelEyebrow}>Module details</p>
            <ModuleNameFields
              name={details.name}
              description={details.description}
              onNameChange={(name) => dispatch({ type: "renamed", name })}
              onDescriptionChange={(description) =>
                dispatch({ type: "described", description })
              }
              nameTaken={nameTaken}
            />
          </section>
          <p className={styles.panelEyebrow}>Field library</p>
          <h2 className={styles.panelHeading}>Add fields</h2>
          <div className={styles.library}>
            <Tabs
              label="Field library"
              defaultTabId="custom"
              tabs={[
                {
                  id: "bound",
                  label: "Databound",
                  panel:
                    offered.length > 0 ? (
                      <div className={styles.controls}>
                        {offered.map((binding) => (
                          <LibraryEntry
                            key={binding}
                            drag={libraryBinding.data(binding)}
                            className={cx(styles.control, styles.boundControl)}
                            disabled={placed.has(binding)}
                            onAdd={() => addBoundField(binding)}
                          >
                            <DatabaseIcon aria-hidden="true" />
                            <span className={styles.boundControlText}>
                              <span>{BINDINGS[binding].label}</span>
                              <span className={styles.boundDescription}>
                                {BINDINGS[binding].description}
                              </span>
                              <span className={styles.boundTags}>
                                <span className={styles.boundTag}>
                                  {FORM_MODE[BINDINGS[binding].formMode].tag}
                                </span>
                                {placed.has(binding) && (
                                  <span className={styles.addedTag}>Added</span>
                                )}
                              </span>
                            </span>
                          </LibraryEntry>
                        ))}
                      </div>
                    ) : (
                      <p className={styles.panelHint}>
                        No data-bound fields for this module yet
                      </p>
                    ),
                },
                {
                  id: "custom",
                  label: "Custom",
                  panel: (
                    <>
                      <p className={styles.panelHint}>
                        Choose a field type, then configure its label.
                      </p>
                      <div className={styles.controls}>
                        {FORM_CONTROLS.map((control) => (
                          <LibraryEntry
                            key={control.key}
                            drag={libraryControl.data(control.key)}
                            className={styles.control}
                            onAdd={() => addField(control.key)}
                          >
                            <PlusIcon aria-hidden="true" />
                            <span>{control.label}</span>
                          </LibraryEntry>
                        ))}
                      </div>
                    </>
                  ),
                },
              ]}
            />
          </div>
        </aside>

        <section
          ref={canvas.ref}
          className={styles.canvas}
          data-drop-over={canvas.over || undefined}
          aria-label="Module canvas"
        >
          <div className={styles.canvasHead}>
            <p className={styles.panelEyebrow}>Module canvas</p>
            <p className={styles.canvasName}>
              {shownName}
              {changed && (
                <span className={styles.unsavedBadge}>Unsaved changes</span>
              )}
            </p>
            <p className={styles.canvasLevel}>{formModule.entityLinkLabel}</p>
          </div>
          {fields.length === 0 ? (
            <div className={styles.emptyState}>
              <LayersIcon aria-hidden="true" />
              <h3>No fields added</h3>
              <p>Choose a field from the library.</p>
            </div>
          ) : (
            <ul className={styles.fields} aria-label="Module fields">
              {fields.map((field, index) => (
                <PlacedFieldCard
                  key={field.id}
                  field={field}
                  selected={field.id === selectedId}
                  needsAttention={needsAttention.has(field.id)}
                  first={index === 0}
                  last={index === fields.length - 1}
                  onSelect={() => dispatch({ type: "selected", id: field.id })}
                  onMove={(by) => dispatch({ type: "moved", index, by })}
                  onRemove={() => dispatch({ type: "removed", id: field.id })}
                />
              ))}
            </ul>
          )}

          <div className={styles.actions}>
            {!saveable && (
              <p id="save-held" className={styles.saveHeld}>
                {saveHeld}
              </p>
            )}
            {unpublished && state.publishedVersion !== null && (
              <DiscardDraftButton
                moduleId={formModule.id}
                revision={state.revision}
                publishedVersion={state.publishedVersion}
                onDiscarded={(module) => adopt(module, "reloaded")}
              />
            )}
            <AdminButton
              variant="outline"
              onClick={() =>
                changed ? setLeaving(true) : navigate(paths.modules)
              }
            >
              Cancel
            </AdminButton>
            <AdminButton
              onClick={() =>
                saveable &&
                save.mutate({
                  sent: { fields, details },
                  revision: state.revision,
                })
              }
              ariaDisabled={save.isPending || !saveable}
              describedBy={saveable ? undefined : "save-held"}
            >
              {save.isPending ? "Saving…" : "Save draft"}
            </AdminButton>
            <PublishButton
              moduleId={formModule.id}
              moduleName={state.storedDetails.name}
              revision={state.revision}
              change={unpublished}
              firstPublish={state.publishedVersion === null}
              unsaved={changed}
              onPublished={(module) => adopt(module, "published")}
            />
          </div>
        </section>

        <aside className={styles.panel} aria-label="Field properties">
          {selected ? (
            <div className={styles.properties}>
              <div className={styles.propertiesTop}>
                <div>
                  <p className={styles.panelEyebrow}>Field properties</p>
                  <h2 className={styles.panelHeading}>{fieldName(selected)}</h2>
                </div>
                <button
                  type="button"
                  className={styles.close}
                  aria-label="Close field properties"
                  onClick={() => dispatch({ type: "deselected" })}
                >
                  <svg viewBox="0 0 16 16" aria-hidden="true">
                    <path
                      d="M4 4l8 8M12 4l-8 8"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
              {isBoundField(selected) ? (
                <BoundFieldProperties
                  field={selected}
                  about={recordOf(instances)}
                  onEdit={(edit) =>
                    dispatch({
                      type: "boundFieldEdited",
                      id: selected.id,
                      edit,
                    })
                  }
                />
              ) : (
                <FieldInspector
                  field={selected}
                  onEdit={(edit) =>
                    dispatch({ type: "edited", id: selected.id, edit })
                  }
                />
              )}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <LayersIcon aria-hidden="true" />
              <h3>Field properties</h3>
              <p>Select a field in the canvas to configure it.</p>
            </div>
          )}
        </aside>
      </div>

      {previewing && (
        <ModulePreviewDialog
          onClose={() => setPreviewing(false)}
          formModule={formModule}
          fields={fields}
        />
      )}
      <DiscardChangesDialog
        open={leaving}
        onKeepEditing={() => setLeaving(false)}
        onDiscard={() => navigate(paths.modules)}
      />
    </>
  )
}
